import {
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ExcelUpload from "../../../utils/components/excelUpload";
import React, { useContext, useEffect } from "react";
import Paper from "@mui/material/Paper";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import { SrfContext } from "./srf.context";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50%",
  height: "70vh",
  backgroundColor: "#fff",
  boxShadow: 24,
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden", // IMPORTANT
  p:4,
};

export const ImportExcelModal = ({
  fixedColumns,
  setReadingRows,
  settings,
  isSRF,
}) => {
  const [open, setOpen] = React.useState(false);
  const [excelArray, setExcelArray] = React.useState([]);
  const [instruments, setInstruments] = React.useState([]);
  const [rejectedData, setRejectedData] = React.useState([]);
  const [remainingData, setRemainingData] = React.useState([]);

  const srfContextValue = useContext(SrfContext);
  const {
    state: { ducInwardRows },
    dispatch,
  } = srfContextValue;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const fetchInstrumentsData = () => {
    axiosWithToken
      .get(
        BASE_URL + "instruments?_fields=id,instrumentName&_where=(status,eq,1)",
      )
      .then((res) => setInstruments(res.data))
      .catch((err) => console.log("instruments data error: ", err));
  };

  function modifyMissingKeysToNull(data) {
    return data.map((obj) => {
      const newObj = {};
      for (const key in obj) {
        newObj[key] = obj[key] !== undefined ? obj[key] : null;
      }
      return newObj;
    });
  }

  const handleSubmit = () => {
    if (isSRF) {
      dispatch({
        field: "ducInwardRows",
        value: [...ducInwardRows, ...modifyMissingKeysToNull(remainingData)],
      });
    } else {
      setReadingRows((prev) => [
        ...prev,
        ...modifyMissingKeysToNull(remainingData),
      ]);
    }
    handleClose();
  };

  useEffect(() => {
    fetchInstrumentsData();
  }, []);

  useEffect(() => {
    let extrsKeys = [];
    if (settings.srfInstrumentsAdditionalColumns?.value) {
      extrsKeys = settings.srfInstrumentsAdditionalColumns.value.split(",");
    }

    const allowedKeys = fixedColumns.map((c) => c.key);

    const fixedColumnsData = excelArray.map((item) => {
      const filteredItem = {};

      allowedKeys.forEach((key) => {
        filteredItem[key] = item.hasOwnProperty(key) ? item[key] : null;
      });

      // normalize numeric columns
      filteredItem.instrumentId =
        typeof filteredItem.instrumentId === "string"
          ? Number(filteredItem.instrumentId.split(",")[0].trim())
          : filteredItem.instrumentId;

      filteredItem.materialType =
        typeof filteredItem.materialType === "string"
          ? filteredItem.materialType.split(",")[0].trim()
          : filteredItem.materialType;

      filteredItem.location =
        typeof filteredItem.location === "string"
          ? filteredItem.location.split(",")[0].trim()
          : filteredItem.location;

      const extraColumns = {};
      extrsKeys.forEach((key) => {
        extraColumns[key.trim()] = item.hasOwnProperty(key.trim())
          ? item[key.trim()]
          : "";
      });

      filteredItem.extraColumns = extraColumns;
      return filteredItem;
    });

    const rejected = [];
    const accepted = [];


    for (const row of fixedColumnsData) {
      const found = instruments.find((i) => i.id === row.instrumentId);

      if (!found) {
        rejected.push({
          ...row,
          rejectionStatus: "Instrument is not found in Instrument database",
        });
      } else {
        accepted.push(row);
      }
    }

    setRejectedData(rejected);
    setRemainingData(accepted);
  }, [excelArray, fixedColumns, instruments]);

  return (
    <div>
      <Button
        id="ducMaster_list_uploadexcel"
        variant="contained"
        onClick={handleOpen}
        size="small"
      >
        Upload Excel File
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          {/* Header with Title, Score, and Close Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            {/* Title */}
            <Typography variant="h6">Upload Excel for SRF</Typography>

            {/* Score */}
            <Typography
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                px: 2,
                py: 0.5,
                borderRadius: "12px",
                fontSize: "13px",
                mr: 1,
              }}
            >
              Accepted: {remainingData.length} | Rejected: {rejectedData.length}
            </Typography>

            {/* X Close Button */}
            <Button
              onClick={handleClose}
              sx={{
                minWidth: "30px",
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#fff",
                backgroundColor: "#d32f2f",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                },
              }}
            >
              X
            </Button>
          </Box>

          <ExcelUpload
            setExcelArray={setExcelArray}
            buttonName="Select Excel File"
          />

          <Typography textAlign="center" margin="15px 0">
            Rejected Excel Data
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr.</TableCell>
                  <TableCell>Instrument ID</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>DUCID</TableCell>
                  <TableCell>Rejection Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rejectedData.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.instrumentId}</TableCell>
                    <TableCell>{row.serialNumber}</TableCell>
                    <TableCell>{row.DUCID}</TableCell>
                    <TableCell>{row.rejectionStatus}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button variant="contained" onClick={handleClose}>
              Cancel
            </Button>

            <Button
              variant="contained"
              disabled={!remainingData.length}
              onClick={handleSubmit}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};
